import { api } from './client';

/**
 * Сводка устройств: у кого доступ на скольких телефонах.
 *
 * ⚠️ ОГРАНИЧЕНИЯ НЕТ — только учёт (решение владельца 31.08.2026). Номер
 * устройства не переживает переустановку приложения, поэтому отказ по такому
 * счётчику бил бы по честным ученикам.
 */
export type DeviceSummary = {
  student_id: string;
  full_name: string;
  email: string;
  device_count: number;
  sessions: number;
};

export type DeviceDetail = {
  device_id: string;
  device_platform: string;
  ip_address: string;
  last_used_at: string;
  sessions: number;
};

/**
 * Один вход ученика: устройство + адрес (D41, договор `devices.md`).
 *
 * ⚠️ ЭТО НЕ ТО ЖЕ, ЧТО `DeviceDetail` ВЫШЕ, И ПУТАТЬ ИХ НЕЛЬЗЯ. Там — живые
 * сессии по номеру устройства приложения: они гаснут при выходе и исчезают.
 * Здесь — все входы с выката учёта, разобранные из user-agent, и они не
 * исчезают. Подменить одно другим значит показать сотруднику не то, что он
 * думает, что видит.
 *
 * Строка — одна на пару «устройство + адрес»: телефон, с которого ученик
 * заходил из дома и из школы, даст две строки.
 */
export type DeviceLoginRow = {
  /** `phone` | `tablet` | `computer` | `other`; набор может расшириться. */
  device_type: string;
  /** `ios` | `android` | `windows` | `macos` | `linux` | `chromeos` | `other`. */
  os: string;
  /** `safari` | `chrome` | `edge` | `yandex` | `firefox` | `opera` | `samsung` | `webview` | `other`. */
  browser: string;
  ip_address: string;
  /** Сколько заходов с этого устройства с этого адреса. Продление сессии не заход. */
  logins: number;
  first_login_at: string;
  last_login_at: string;
};

export const devicesApi = {
  list: (min?: number) =>
    api
      .get<{ items: DeviceSummary[]; threshold: number }>('/admin/devices', {
        params: min ? { min } : undefined,
      })
      .then((r) => r.data),

  detail: (studentId: string) =>
    api.get<{ items: DeviceDetail[] }>(`/admin/devices/${studentId}`).then((r) => r.data),

  /**
   * Входы ученика по устройству и адресу — «устройство · IP · заходов ·
   * последний раз» в карточке ученика у сотрудника.
   *
   * Роли шире, чем у сводки: сюда пускают и куратора (решение владельца
   * 12.09.2026), а `detail` и `reset` — по-прежнему нет.
   *
   * ⚠️ Строк не больше 100, `total` говорит, сколько их всего: у активного
   * ученика с мобильного интернета адрес меняется по нескольку раз на дню.
   */
  logins: (studentId: string) =>
    api
      .get<{ items: DeviceLoginRow[]; total: number }>(`/admin/devices/${studentId}/logins`)
      .then((r) => r.data),

  reset: (studentId: string) =>
    api.post<{ revoked: number }>(`/admin/devices/${studentId}/reset`).then((r) => r.data),
};

/**
 * Подпись устройства для человека.
 *
 * ⚠️ ПУСТОЙ НОМЕР — ЭТО НЕ ОШИБКА. Так выглядят сессии из старых сборок, которые
 * номер ещё не шлют. Показывать пустоту нельзя: администратор решит, что
 * сломалось.
 */
export function deviceLabel(deviceId: string, unknownLabel: string): string {
  if (!deviceId) return unknownLabel;
  // Полный номер человеку ни о чём не говорит и занимает всю строку.
  return deviceId.slice(0, 8);
}
